import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { AddressFormModal } from '../components/AddressFormModal';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getAddresses, deleteAddress, setDefaultAddress } from '../api/client';
import type { BuyerAddress } from '../types';
import { Plus, MapPin, Check, Trash2, Edit2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function ShippingAddressesPage() {
  useDocumentTitle('Shipping Addresses');
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<BuyerAddress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<BuyerAddress | null>(null);

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAddresses();
      data.sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1));
      setAddresses(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load addresses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetDefault = async (addressId: string) => {
    try {
      await setDefaultAddress(addressId);
      await loadAddresses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set default address');
    }
  };

  const handleDelete = async (addressId: string) => {
    if (!confirm('Are you sure you want to delete this address?')) return;
    try {
      await deleteAddress(addressId);
      await loadAddresses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete address');
    }
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setIsModalOpen(true);
  };

  const handleEdit = (address: BuyerAddress) => {
    setEditingAddress(address);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingAddress(null);
  };

  const handleAddressSaved = () => {
    setIsModalOpen(false);
    setEditingAddress(null);
    loadAddresses();
  };

  const renderAddressCard = (address: BuyerAddress) => (
    <div key={address.id} style={styles.addressCard}>
      <div style={styles.addressHeader}>
        <div style={styles.addressIcon}>
          <MapPin size={20} color={colors.brand.blue} />
        </div>
        <div style={styles.addressInfo}>
          <div style={styles.addressName}>
            {address.name}
            {address.isDefault && (
              <span style={styles.defaultBadge}>Default</span>
            )}
          </div>
          <div style={styles.addressLine}>{address.streetLine1}</div>
          {address.streetLine2 && (
            <div style={styles.addressLine}>{address.streetLine2}</div>
          )}
          <div style={styles.addressLine}>
            {address.city}, {address.state} {address.zipCode}
          </div>
        </div>
      </div>
      <div style={styles.addressActions}>
        {!address.isDefault && (
          <button
            style={styles.actionButton}
            onClick={() => handleSetDefault(address.id)}
            title="Set as default"
          >
            <Check size={18} />
            Set Default
          </button>
        )}
        <button
          style={styles.actionButton}
          onClick={() => handleEdit(address)}
          title="Edit"
        >
          <Edit2 size={18} />
          Edit
        </button>
        <button
          style={{ ...styles.actionButton, ...styles.deleteButton }}
          onClick={() => handleDelete(address.id)}
          title="Delete"
        >
          <Trash2 size={18} />
          Delete
        </button>
      </div>
    </div>
  );

  return (
    <Layout>
      <div style={styles.container}>
        <div style={styles.content}>
          <button style={styles.backButton} onClick={() => navigate('/account')}>
            <ArrowLeft size={18} /> Back to Account
          </button>
          <h1 style={styles.title}>Shipping Addresses</h1>

          {error && <div style={styles.error}>{error}</div>}

          {isLoading ? (
            <div style={styles.loading}>Loading addresses...</div>
          ) : (
            <>
              <button style={styles.addButton} onClick={handleAddNew}>
                <Plus size={20} />
                Add New Address
              </button>

              {addresses.length === 0 ? (
                <div style={styles.emptyState}>
                  <MapPin size={48} color={colors.gray[400]} />
                  <p style={styles.emptyText}>No shipping addresses yet</p>
                  <p style={styles.emptySubtext}>
                    Add a shipping address to place bids on items
                  </p>
                </div>
              ) : (
                <div style={styles.addressList}>
                  {addresses.map(renderAddressCard)}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isModalOpen && (
        <AddressFormModal
          address={editingAddress}
          onClose={handleModalClose}
          onSaved={handleAddressSaved}
        />
      )}
    </Layout>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
  },
  content: {
    maxWidth: '600px',
    margin: '0 auto',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    fontSize: 14,
    fontWeight: 500,
    padding: 0,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: 24,
  },
  error: {
    padding: '12px 16px',
    marginBottom: '16px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '14px',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '14px',
    marginBottom: '24px',
    fontSize: '15px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
  },
  addressList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  addressCard: {
    padding: '16px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  addressHeader: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
  },
  addressIcon: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.brand.blue10,
    borderRadius: '10px',
    flexShrink: 0,
  },
  addressInfo: {
    flex: 1,
  },
  addressName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '16px',
    fontWeight: 600,
    color: colors.ink.black,
    marginBottom: '4px',
  },
  defaultBadge: {
    fontSize: '11px',
    fontWeight: 600,
    color: colors.accent.purple,
    backgroundColor: '#EDE9FE',
    padding: '2px 8px',
    borderRadius: '100px',
  },
  addressLine: {
    fontSize: '14px',
    color: colors.gray[600],
    lineHeight: 1.5,
  },
  addressActions: {
    display: 'flex',
    gap: '8px',
    paddingTop: '12px',
    borderTop: `1px solid ${colors.gray[100]}`,
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.gray[600],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  deleteButton: {
    color: '#DC2626',
    backgroundColor: '#FEE2E2',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 24px',
    backgroundColor: colors.white,
    borderRadius: '12px',
    border: `1px solid ${colors.gray[300]}`,
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
    marginTop: '16px',
    marginBottom: '4px',
  },
  emptySubtext: {
    fontSize: '14px',
    color: colors.gray[600],
    textAlign: 'center',
  },
};
